/**
 * Help anchors: the ONE catalog of deep-linkable places in the app.
 *
 * Help articles (central `kb_articles` / `kb_faqs`) link straight to a
 * setting with a URL like
 *
 *   [Past-due reminders](/{{club_id}}/manage/settings#past-due-reminders)
 *
 * Every id after the `#` must be listed here. Each entry says:
 *   - route      the App.tsx route that renders it (with `:clubid`)
 *   - label      what staff see on screen, for article writers
 *   - component  the component that renders the `data-help-anchor`
 *                attribute (tests render that component and look for it)
 *   - parent     the collapsed section / tab that must be opened before the
 *                anchor exists in the page (optional)
 *
 * Components mark the element with `{...helpAnchor("past-due-reminders")}`.
 * The id argument is typed to this catalog, so a typo fails `tsc`.
 *
 * `scripts/check-help-links.js` reads this file to check article links, and
 * `src/help/__tests__/helpAnchors.test.ts` checks every id is rendered by
 * its component. Ids are stable: once an article links to one, renaming it
 * breaks that article. Add new ids freely; rename only with the checker.
 */

export interface HelpAnchorEntry {
  route: string;
  label: string;
  component: string;
  parent?: string;
}

function defineHelpAnchors<T extends Record<string, HelpAnchorEntry>>(anchors: T): T {
  return anchors;
}

const GENERAL = "/:clubid/manage/settings";
const FEES = "/:clubid/manage/settings/fees";
const EMAIL = "/:clubid/manage/settings/email";
const EMAIL_TEMPLATES = "/:clubid/manage/settings/email-templates";
const KIOSK = "/:clubid/manage/settings/kiosk";
const INTEGRATIONS = "/:clubid/manage/settings/integrations";
const INACTIVITY = "/:clubid/manage/settings/inactivity-automation";
const PRICING = "/:clubid/manage/settings/pricing";
const STORE = "/:clubid/manage/settings/store";
const SUPPORT = "/:clubid/manage/settings/support";
const WAIVERS = "/:clubid/manage/settings/waivers";
const CALL_ROUTING = "/:clubid/manage/ai-receptionist/call-routing";

export const HELP_ANCHORS = defineHelpAnchors({
  // ── General settings (/manage/settings) ──────────────────────────────
  "general-settings": { route: GENERAL, label: "General Settings", component: "ManageSettingsPage" },
  "past-due-reminders": { route: GENERAL, label: "Send Automated Past-Due Reminders", component: "ManageSettingsPage", parent: "general-settings" },
  "other-gender-option": { route: GENERAL, label: "Enable Other Gender Option", component: "ManageSettingsPage", parent: "general-settings" },
  "enroll-reactivate-payment": { route: GENERAL, label: "Enable Athlete Enroll Reactive Payment Option", component: "ManageSettingsPage", parent: "general-settings" },
  "require-check-number": { route: GENERAL, label: "Require Check Number on Check Payments", component: "ManageSettingsPage", parent: "general-settings" },
  "organization-timezone": { route: GENERAL, label: "Organization Timezone", component: "ManageSettingsPage", parent: "general-settings" },
  "brand-settings": { route: GENERAL, label: "Brand & Theme Settings", component: "ManageSettingsPage" },
  "club-logo": { route: GENERAL, label: "Upload Logo", component: "BrandSettingsSection", parent: "brand-settings" },
  "business-name": { route: GENERAL, label: "Business Name", component: "BrandSettingsSection", parent: "brand-settings" },
  "tax-entity-type": { route: GENERAL, label: "Tax Entity Type", component: "BrandSettingsSection", parent: "brand-settings" },
  "stripe-reconciliation-alerts": { route: GENERAL, label: "Stripe payout reconciliation alerts", component: "BrandSettingsSection", parent: "brand-settings" },
  "contact-info": { route: GENERAL, label: "Contact information (address, email, phone)", component: "BrandSettingsSection", parent: "brand-settings" },
  "social-links": { route: GENERAL, label: "Social links", component: "BrandSettingsSection", parent: "brand-settings" },
  "footer-text": { route: GENERAL, label: "Footer Text", component: "BrandSettingsSection", parent: "brand-settings" },
  "about-us": { route: GENERAL, label: "About Us (Full)", component: "BrandSettingsSection", parent: "brand-settings" },
  "brand-colors": { route: GENERAL, label: "Primary and Secondary Color", component: "BrandSettingsSection", parent: "brand-settings" },
  "website-seo": { route: GENERAL, label: "Website & SEO Settings", component: "ManageSettingsPage" },
  "negative-balance": { route: GENERAL, label: "Negative Balance Settings", component: "ManageSettingsPage" },
  "slot-capacity-alerts": { route: GENERAL, label: "Slot Capacity Alerts", component: "ManageSettingsPage" },
  "lead-management": { route: GENERAL, label: "Lead Management", component: "ManageSettingsPage" },
  "default-lead-assignee": { route: GENERAL, label: "Default Lead Assignee", component: "ManageSettingsPage", parent: "lead-management" },
  "trial-signup": { route: GENERAL, label: "Trial Signup Settings", component: "ManageSettingsPage" },
  "confirmation-page": { route: GENERAL, label: "Registration Confirmation Page", component: "ManageSettingsPage" },
  "advanced-settings": { route: GENERAL, label: "Advanced Settings", component: "ManageSettingsPage" },
  "config-review-cadence": { route: GENERAL, label: "Config Review Cadence", component: "ManageSettingsPage", parent: "advanced-settings" },
  "stripe-api-keys": { route: GENERAL, label: "Stripe Public and Secret API Keys", component: "ManageSettingsPage", parent: "advanced-settings" },
  "stripe-connect": { route: GENERAL, label: "Stripe Connect", component: "ManageSettingsPage", parent: "advanced-settings" },
  "system-updates": { route: GENERAL, label: "System Updates", component: "ManageSettingsPage" },
  "data-maintenance": { route: GENERAL, label: "Data Maintenance", component: "ManageSettingsPage" },

  // Advanced settings (inside the collapsed "Advanced Settings" section)
  "program-product-conversion": { route: GENERAL, label: "Enable Program to Product Conversion", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "zero-balance-to-reactivate": { route: GENERAL, label: "Require Zero Balance to Reactivate", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "member-self-unenroll": { route: GENERAL, label: "Allow Members to Unenroll Their Own Athletes", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "payment-method-routing": { route: GENERAL, label: "Payment Method Routing", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "invoice-email-responsible-person": { route: GENERAL, label: "Send Invoice Emails to the Responsible Person", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "staff-email-sign-in-link": { route: GENERAL, label: "Allow Staff to Sign In by Emailed Link", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "participant-terminology": { route: GENERAL, label: "Participant Terminology", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "attire-sizes": { route: GENERAL, label: "Attire & Uniform Sizes", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "attire-team-only": { route: GENERAL, label: "Attire sizes: team athletes only", component: "AttireFieldsEditor", parent: "attire-sizes" },
  "makeup-classes": { route: GENERAL, label: "Allow Makeup Classes", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "required-athlete-fields": { route: GENERAL, label: "Required Athlete Fields", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "staff-default-home": { route: GENERAL, label: "Staff Default Home", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "attendance-mode": { route: GENERAL, label: "Attendance Mode", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "enforce-class-capacity": { route: GENERAL, label: "Enforce Class Capacity", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "prorate-on-enroll": { route: GENERAL, label: "Pro-rate invoice on staff enroll", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "annual-fee-on-enroll": { route: GENERAL, label: "Assess annual fee on enroll", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "annual-fee-cadence": { route: GENERAL, label: "Annual fee cadence", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "capacity-oversell": { route: GENERAL, label: "Slot Capacity Oversell", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "registration-form-style": { route: GENERAL, label: "Registration Form (step by step or one page)", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "registration-phone-address": { route: GENERAL, label: "Require Phone + Address on Registration", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "age-check-mode": { route: GENERAL, label: "Age Check Mode", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "storefront-visibility": { route: GENERAL, label: "Storefront Visibility", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "owner-sms-notifications": { route: GENERAL, label: "Owner SMS Notifications", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "public-programs-layout": { route: GENERAL, label: "Public Programs Page Layout", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "post-settings": { route: GENERAL, label: "Posts (comments, reactions, moderation)", component: "AdvancedSettingsToggles", parent: "advanced-settings" },
  "localization-currency": { route: GENERAL, label: "Localization & Currency", component: "AdvancedSettingsToggles", parent: "advanced-settings" },

  // ── Fees (/manage/settings/fees) ─────────────────────────────────────
  "sales-tax": { route: FEES, label: "Tax toggle", component: "FeeSettingsForm" },
  "credit-card-fee": { route: FEES, label: "Credit card fee", component: "FeeSettingsForm" },
  "pass-fees-to-customer": { route: FEES, label: "Pass card and bank fees to the customer", component: "FeeSettingsForm" },
  "sibling-discount": { route: FEES, label: "Sibling discount", component: "FeeSettingsForm" },
  "annual-fee-billing": { route: FEES, label: "Annual fee billing model", component: "FeeSettingsForm" },
  "makeup-drop-in-rate": { route: FEES, label: "Makeup drop-in rate ($/session)", component: "FeeSettingsForm" },

  // ── Email settings (/manage/settings/email) ──────────────────────────
  "missing-attendance-notifications": { route: EMAIL, label: "Missing Attendance Notifications", component: "EmailSettingsPage" },
  "additional-notification-recipients": { route: EMAIL, label: "Additional Notification Recipients", component: "EmailSettingsPage" },

  // ── Email templates (/manage/settings/email-templates) — tabs ────────
  "email-header": { route: EMAIL_TEMPLATES, label: "Email Header tab", component: "EmailTemplatesPage" },
  "email-footer": { route: EMAIL_TEMPLATES, label: "Email Footer tab", component: "EmailTemplatesPage" },
  "email-templates": { route: EMAIL_TEMPLATES, label: "Email Templates tab", component: "EmailTemplatesPage" },

  // ── Kiosk (/manage/settings/kiosk) ───────────────────────────────────
  "kiosk-url": { route: KIOSK, label: "Kiosk URL", component: "KioskSettingsPage" },
  "kiosk-pin": { route: KIOSK, label: "PIN Code", component: "KioskSettingsPage" },
  "kiosk-devices": { route: KIOSK, label: "Registered Devices", component: "KioskSettingsPage" },
  "kiosk-troubleshooting": { route: KIOSK, label: "Troubleshooting", component: "KioskSettingsPage" },

  // ── Integrations (/manage/settings/integrations) ─────────────────────
  "registration-wizard": { route: INTEGRATIONS, label: "Registration Form embed (registration wizard)", component: "IntegrationsSettingsPage" },
  "embed-styling": { route: INTEGRATIONS, label: "Embed Styling", component: "IntegrationsSettingsPage" },
  "embed-code": { route: INTEGRATIONS, label: "Embed code", component: "IntegrationsSettingsPage" },
  "embed-preview": { route: INTEGRATIONS, label: "Preview", component: "IntegrationsSettingsPage" },
  "embed-events": { route: INTEGRATIONS, label: "Embed events", component: "IntegrationsSettingsPage" },

  // ── Inactivity automation (/manage/settings/inactivity-automation) ───
  "inactivity-automation": { route: INACTIVITY, label: "Enable inactivity automation", component: "InactivitySettingsPage" },
  "inactivity-thresholds": { route: INACTIVITY, label: "Escalation thresholds", component: "InactivitySettingsPage" },

  // ── Pricing (/manage/settings/pricing) ───────────────────────────────
  "pricing-tiers": { route: PRICING, label: "Pricing Tiers", component: "ProgramPricingSettings" },
  "program-configuration": { route: PRICING, label: "Program Configuration", component: "ProgramPricingSettings" },

  // ── Store (/manage/settings/store) ───────────────────────────────────
  "store-inventory": { route: STORE, label: "Inventory", component: "StoreSettingsPage" },

  // ── Support routing (/manage/settings/support) ───────────────────────
  "comprehensive-support": { route: SUPPORT, label: "Club Central handles all support", component: "SupportSettingsPage" },

  // ── Waivers (/manage/settings/waivers) ───────────────────────────────
  "new-waiver": { route: WAIVERS, label: "New Waiver", component: "WaiversPage" },
  "missing-waivers": { route: WAIVERS, label: "Missing waivers", component: "WaiversPage" },
  "missing-medical-info": { route: WAIVERS, label: "Missing medical information", component: "WaiversPage" },
  // ── Call routing (/manage/ai-receptionist/call-routing) ──────────────
  "call-routing-enabled": { route: CALL_ROUTING, label: "Ring my team when the AI hands off a call", component: "CallRoutingSettingsPage" },
  "call-routing-groups": { route: CALL_ROUTING, label: "Call groups", component: "CallRoutingSettingsPage" },
  "test-call-alerts": { route: CALL_ROUTING, label: "Test my call alerts", component: "CallRoutingSettingsPage" },
});

export type HelpAnchorId = keyof typeof HELP_ANCHORS;

const ANCHOR_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isHelpAnchorId(id: string): id is HelpAnchorId {
  return Object.prototype.hasOwnProperty.call(HELP_ANCHORS, id);
}

/** Props that mark an element as the target of `#id`. */
export function helpAnchor(id: HelpAnchorId): { "data-help-anchor": HelpAnchorId } {
  return { "data-help-anchor": id };
}

/**
 * The anchor and every section above it, outermost first:
 * helpAnchorChain("attire-team-only") →
 *   ["advanced-settings", "attire-sizes", "attire-team-only"].
 * An id outside the catalog is its own one-element chain, so plain
 * `#heading` ids still scroll.
 */
export function helpAnchorChain(id: string): string[] {
  const chain: string[] = [];
  let current: string | undefined = id;
  const seen = new Set<string>();
  while (current && !seen.has(current)) {
    seen.add(current);
    chain.unshift(current);
    current = isHelpAnchorId(current)
      ? (HELP_ANCHORS[current] as HelpAnchorEntry).parent
      : undefined;
  }
  return chain;
}

/** True when `id` looks like an anchor id (kebab-case, lower case). */
export function isWellFormedHelpAnchorId(id: string): boolean {
  return ANCHOR_ID_PATTERN.test(id);
}

/** The in-app URL for an anchor at a club: `/dev-tenant/manage/settings#past-due-reminders`. */
export function helpAnchorHref(id: HelpAnchorId, clubId: string): string {
  const route = HELP_ANCHORS[id].route.replace(":clubid", clubId);
  return `${route}#${id}`;
}
